import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { queryKeys } from "@/queryKeys";
import { borrowerService } from "@/services/borrowers";
import { Borrower, CreateBorrowerData } from "@/types/borrower";

interface UseCreateBorrowerReturn {
  create: (data: CreateBorrowerData) => Promise<Borrower>;
  loading: boolean;
  error: string | null;
}

export const useCreateBorrower = (): UseCreateBorrowerReturn => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: CreateBorrowerData) => borrowerService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.borrowers() });
    },
  });

  const create = useCallback(
    async (data: CreateBorrowerData): Promise<Borrower> => {
      try {
        return await mutation.mutateAsync(data);
      } catch (err: unknown) {
        throw err;
      }
    },
    [mutation]
  );

  const error =
    mutation.error != null
      ? ((mutation.error as Error).message ?? "Failed to create borrower")
      : null;

  return {
    create,
    loading: mutation.isPending,
    error,
  };
};
